import { DateRangeID, TimeRange } from '@fullstory/client-api';
import { intl } from '@fullstory/intl';
import { TimeInterval, TimeRange as SegmentTimeRange } from '@fullstory/proto/fs/api/segment';
import {
  TimeRangeComparison,
  TrendFrequency,
} from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { useFlags } from '@fullstory/react-hooks';
import { dateRangeToTimeRange } from '@fullstory/redux';
import { dateRangeById, getDateRangeIdFromTimeRange } from '@fullstory/search-model';
import { assertExhaustive, isNonNull } from '@fullstory/typeutils';
import { dateRangeToDates, getAdjustedEndDate } from '@fullstory/util/date';
import moment from 'moment';
import { useMemo } from 'react';
import { defineMessage } from 'react-intl';
import { DateRangeOption } from '../types/dateRange';
import { getComparisonTimeRange } from './comparison';
import { FREQUENCY_TO_INTERVAL, INTERVAL_TO_FREQUENCY } from './metrics';

export const getDateStringFromRange = (range: TimeRange): string => {
  const dateRangeId = getDateRangeIdFromTimeRange(range);
  return getDateStringFromDateRangeId(dateRangeId, range);
};

export const getDateStringFromDateRangeId = (id: DateRangeID, range: TimeRange): string => {
  if (id !== DateRangeID.Since && id !== DateRangeID.Custom && dateRangeById[id]) {
    return dateRangeById[id].name;
  } else {
    return getNumericCustomDateString(id, range);
  }
};

const sinceDateFormat = defineMessage({
  defaultMessage: 'Since {date, date, ::Mdyy}',
  id: 'WxxgMS',
});
const sinceDateFormatShort = defineMessage({
  defaultMessage: 'Since {date, date, ::Md}',
  id: 'eEXGrV',
});

export const getNumericCustomDateString = (id: DateRangeID, range: TimeRange): string => {
  const sinceDate = new Date(range.Start);
  const endDate = getAdjustedEndDate(new Date(range.End)).toDate();
  const now = new Date();

  if (id === DateRangeID.Since && sinceDate) {
    const format =
      sinceDate.getFullYear() != now.getFullYear() ? sinceDateFormat : sinceDateFormatShort;
    return intl.formatMessage(format, { date: sinceDate });
  }

  const sameYear =
    sinceDate.getFullYear() === endDate.getFullYear() &&
    sinceDate.getFullYear() === now.getFullYear();
  return intl.formatDateTimeRange(sinceDate, endDate, {
    month: 'numeric',
    day: 'numeric',
    year: sameYear ? undefined : '2-digit',
  });
};

export function timeRangeToDates(range: SegmentTimeRange) {
  const { startTime, endTime } = range;
  const dateRangeId = getDateRangeIdFromTimeRange({
    Start: startTime ?? '',
    End: endTime ?? '',
  });
  return dateRangeToDates(dateRangeId, new Date(), startTime, endTime);
}

export const getDefaultTimeRange = (
  orgUseWeeklyEveryone: boolean,
  frequency?: TrendFrequency,
): SegmentTimeRange => {
  // default to past month
  let dateRangeId = dateRangeById[DateRangeID.PastMonth];
  if (frequency) {
    switch (frequency) {
      case TrendFrequency.TREND_FREQUENCY_MINUTELY:
        dateRangeId = dateRangeById[DateRangeID.Past60Minutes];
        break;

      // hourly defaults to past 24 hours
      case TrendFrequency.TREND_FREQUENCY_HOURLY:
        dateRangeId = dateRangeById[DateRangeID.Past24Hours];
        break;

      // weekly & monthly to past 90 days
      case TrendFrequency.TREND_FREQUENCY_WEEKLY:
      case TrendFrequency.TREND_FREQUENCY_MONTHLY:
        dateRangeId = dateRangeById[DateRangeID.Past90Days];
        break;

      case TrendFrequency.TREND_FREQUENCY_DAILY:
        // if the org has the use weekly everyone flag on, override to past week
        if (orgUseWeeklyEveryone) {
          dateRangeId = dateRangeById[DateRangeID.PastWeek];
        }
        break;
      default:
        assertExhaustive(frequency);
    }
  }
  return dateRangeToTimeRange(dateRangeId);
};

export const getCustomDateRangeLength = (timeRange: TimeRange | null): number =>
  moment(timeRange?.End).diff(moment(timeRange?.Start), 'days');

// We know that the end date of `Since` is today, so count the number of days between
// the start date and the current date
export const getSinceDateRangeLength = (timeRange: TimeRange | null): number =>
  moment(new Date()).diff(moment(timeRange?.Start), 'days');

export const getDefaultFrequencyForTimeRange = (
  dateRangeId: DateRangeID,
  timeRange: TimeRange | null,
  currentCardFrequency?: TrendFrequency,
): TrendFrequency | undefined => {
  let customDateRangeLength;
  switch (dateRangeId) {
    case DateRangeID.Past60Minutes:
    case DateRangeID.Past3Hours:
    case DateRangeID.Past6Hours:
      return TrendFrequency.TREND_FREQUENCY_MINUTELY;
    case DateRangeID.Today:
    case DateRangeID.Yesterday:
    case DateRangeID.Past24Hours:
      return TrendFrequency.TREND_FREQUENCY_HOURLY;
    case DateRangeID.PastWeek:
      return TrendFrequency.TREND_FREQUENCY_DAILY;
    case DateRangeID.PastMonth:
      if (currentCardFrequency === TrendFrequency.TREND_FREQUENCY_WEEKLY) {
        return TrendFrequency.TREND_FREQUENCY_WEEKLY;
      } else {
        return TrendFrequency.TREND_FREQUENCY_DAILY;
      }
    case DateRangeID.Past90Days:
    case DateRangeID.PastYear:
      if (currentCardFrequency === TrendFrequency.TREND_FREQUENCY_MONTHLY) {
        return TrendFrequency.TREND_FREQUENCY_MONTHLY;
      } else {
        return TrendFrequency.TREND_FREQUENCY_WEEKLY;
      }
    case DateRangeID.Since:
    case DateRangeID.Custom:
      customDateRangeLength =
        dateRangeId === DateRangeID.Custom
          ? getCustomDateRangeLength(timeRange)
          : getSinceDateRangeLength(timeRange);
      if (customDateRangeLength <= 1) {
        return TrendFrequency.TREND_FREQUENCY_HOURLY;
      } else if (customDateRangeLength < 7) {
        return TrendFrequency.TREND_FREQUENCY_DAILY;
      } else if (customDateRangeLength >= 7 && customDateRangeLength <= 30) {
        if (currentCardFrequency === TrendFrequency.TREND_FREQUENCY_WEEKLY) {
          return TrendFrequency.TREND_FREQUENCY_WEEKLY;
        } else {
          return TrendFrequency.TREND_FREQUENCY_DAILY;
        }
      } else {
        // Custom date range > 30 days
        if (currentCardFrequency === TrendFrequency.TREND_FREQUENCY_MONTHLY) {
          return TrendFrequency.TREND_FREQUENCY_MONTHLY;
        }
        return TrendFrequency.TREND_FREQUENCY_WEEKLY;
      }
    case DateRangeID.AllTime:
      // All Time is not currently supported so we should not reach this case
      // If we do reach this case, just don't modify card frequency
      return currentCardFrequency;
    default:
      assertExhaustive(dateRangeId);
  }
};

export const getDefaultIntervalForTimeRange = (
  timeRange: SegmentTimeRange | null,
  currentCardInterval?: TimeInterval,
): TimeInterval | undefined => {
  const timeRangeForDateRangeId = timeRange
    ? {
        Start: timeRange.startTime,
        End: timeRange.endTime,
      }
    : null;
  const dateRangeId = timeRangeForDateRangeId
    ? getDateRangeIdFromTimeRange(timeRangeForDateRangeId)
    : DateRangeID.PastMonth;
  const frequency = getDefaultFrequencyForTimeRange(
    dateRangeId,
    timeRangeForDateRangeId,
    currentCardInterval ? INTERVAL_TO_FREQUENCY[currentCardInterval] : undefined,
  );
  return frequency ? FREQUENCY_TO_INTERVAL[frequency] : currentCardInterval;
};

// Helper to return the past time range for any library object
export const usePastTimeRange = (
  timeRange: SegmentTimeRange | null,
  timeRangeComparison: TimeRangeComparison | null,
) => {
  const comparisonEnabled = isNonNull(timeRangeComparison);

  const now = useMemo(() => new Date(), []);
  const pastTimeRange = useMemo(
    () =>
      comparisonEnabled && timeRange && timeRangeComparison
        ? getComparisonTimeRange(timeRange, timeRangeComparison, now)
        : null,
    [comparisonEnabled, timeRange, timeRangeComparison, now],
  );
  return {
    pastTimeRange,
    comparisonEnabled,
  };
};

const minutelyTimePeriodOptions: DateRangeOption[] = [
  {
    id: DateRangeID.Past60Minutes,
    label: intl.formatMessage({ defaultMessage: 'Past 60 minutes', id: 'dycj1L' }),
    days: 1.0 / 24.0,
  },
];

const todayYesterdayTimePeriodOptions: DateRangeOption[] = [
  {
    id: DateRangeID.Today,
    label: intl.formatMessage({ defaultMessage: 'Today', id: 'zWgbGg' }),
    days: 1,
  },
  {
    id: DateRangeID.Yesterday,
    label: intl.formatMessage({ defaultMessage: 'Yesterday', id: '6dIxDP' }),
    days: 1,
  },
];

const smallHourlyTimePeriodOptions: DateRangeOption[] = [
  {
    id: DateRangeID.Past3Hours,
    label: intl.formatMessage({ defaultMessage: 'Past 3 hours', id: 'f9/17e' }),
    days: 3.0 / 24.0,
  },
  {
    id: DateRangeID.Past6Hours,
    label: intl.formatMessage({ defaultMessage: 'Past 6 hours', id: '/WVb/X' }),
    days: 6.0 / 24.0,
  },
];

const largeHourlyTimePeriodOptions: DateRangeOption[] = [
  {
    id: DateRangeID.Past24Hours,
    label: intl.formatMessage({ defaultMessage: 'Past 24 hours', id: '/a7cg5' }),
    days: 1,
  },
];

const dailyTimePeriodOptions: DateRangeOption[] = [
  {
    id: DateRangeID.PastWeek,
    label: intl.formatMessage({ defaultMessage: 'Past 7 days', id: 'mbbNJS' }),
    days: 7,
  },
];

const weeklyTimePeriodOptions: DateRangeOption[] = [
  {
    id: DateRangeID.PastMonth,
    label: intl.formatMessage({ defaultMessage: 'Past 30 days', id: 'OIM20v' }),
    days: 30,
  },
];
const monthlyTimePeriodOptions: DateRangeOption[] = [
  {
    id: DateRangeID.Past90Days,
    label: intl.formatMessage({ defaultMessage: 'Past 90 days', id: 'ZOWtIg' }),
    days: 90,
  },
  {
    id: DateRangeID.PastYear,
    label: intl.formatMessage({ defaultMessage: 'Past 12 months', id: 'o0vS8L' }),
    days: 365,
  },
];

const customTimePeriodOptions: DateRangeOption[] = [
  {
    id: DateRangeID.Since,
    label: intl.formatMessage({ defaultMessage: 'Since', id: '+eH89J' }),
    days: 0,
  },
  {
    id: DateRangeID.Custom,
    label: intl.formatMessage({ defaultMessage: 'Custom', id: 'Sjo1P4' }),
    days: 0,
  },
];

const allTimeOption: DateRangeOption = {
  id: DateRangeID.AllTime,
  label: intl.formatMessage({ defaultMessage: 'All Time', id: '52UMW3' }),
  days: Number.POSITIVE_INFINITY,
};

const defaultParams = {
  timeInterval: TimeInterval.TIME_INTERVAL_ENTIRE_RANGE,
  // Optional param to further restrict time range options if AppDatePickerAlternateOptions is tru
  restrictLargeOrgs: false,
  includeAllTime: false,
};

export const useTimeRangeOptions = ({
  timeInterval,
  restrictLargeOrgs,
  includeAllTime,
}: Partial<typeof defaultParams> = defaultParams) => {
  const baseParams = useBaseTimeRangeOptionsParams({
    restrictLargeOrgs,
    includeAllTime,
  });

  return useMemo(
    () =>
      getTimeRangeOptions({
        ...baseParams,
        timeInterval: timeInterval ?? defaultParams.timeInterval,
      }),
    [timeInterval, baseParams],
  );
};

export function useBaseTimeRangeOptionsParams({
  restrictLargeOrgs,
  includeAllTime,
}: Partial<Omit<typeof defaultParams, 'timeInterval'>>): Omit<
  TimeRangeOptionsParams,
  'timeInterval'
> {
  const flags = useFlags();
  const maxDays = flags.featureFlags().AppDateRangeMaxLength;
  const showAlternateOptions = flags.featureFlags().AppDatePickerAlternateOptions;
  const includeMinutely = flags.featureFlags().AppTrendsMinutely;
  const includePast7DaysHourly = flags.featureFlags().AppExpandedHourly;

  return useMemo(
    () => ({
      restrictLargeOrgs: restrictLargeOrgs ?? defaultParams.restrictLargeOrgs,
      includeAllTime: includeAllTime ?? defaultParams.includeAllTime,
      includeMinutely,
      showAlternateOptions,
      includePast7DaysHourly,
      maxDays,
    }),
    [
      restrictLargeOrgs,
      includeAllTime,
      includeMinutely,
      showAlternateOptions,
      includePast7DaysHourly,
      maxDays,
    ],
  );
}

export interface TimeRangeOptionsParams {
  timeInterval: TimeInterval;
  restrictLargeOrgs: boolean;
  includeAllTime: boolean;
  includeMinutely: boolean;
  includePast7DaysHourly: boolean;
  showAlternateOptions: boolean;
  maxDays: number;
}

export function getTimeRangeOptions({
  timeInterval,
  restrictLargeOrgs,
  includeAllTime,
  includeMinutely,
  showAlternateOptions,
  includePast7DaysHourly,
  maxDays,
}: TimeRangeOptionsParams) {
  let timePeriodOptions: DateRangeOption[] = [];
  switch (timeInterval) {
    case TimeInterval.TIME_INTERVAL_MINUTE:
      timePeriodOptions = [
        ...minutelyTimePeriodOptions,
        ...smallHourlyTimePeriodOptions,
        ...customTimePeriodOptions,
      ];
      break;
    case TimeInterval.TIME_INTERVAL_HOUR:
      timePeriodOptions = [
        ...todayYesterdayTimePeriodOptions,
        ...(includeMinutely ? smallHourlyTimePeriodOptions : []),
        ...largeHourlyTimePeriodOptions,
        ...(includePast7DaysHourly ? dailyTimePeriodOptions : []),
        ...customTimePeriodOptions,
      ];
      break;
    case TimeInterval.TIME_INTERVAL_DAY:
      timePeriodOptions = [
        ...dailyTimePeriodOptions,
        ...weeklyTimePeriodOptions,
        ...monthlyTimePeriodOptions,
        ...customTimePeriodOptions,
      ];
      break;
    case TimeInterval.TIME_INTERVAL_WEEK:
      timePeriodOptions = [
        ...weeklyTimePeriodOptions,
        ...monthlyTimePeriodOptions,
        ...customTimePeriodOptions,
      ];
      break;
    case TimeInterval.TIME_INTERVAL_MONTH:
      timePeriodOptions = [
        ...weeklyTimePeriodOptions,
        ...monthlyTimePeriodOptions,
        ...customTimePeriodOptions,
      ];
      break;
    case TimeInterval.TIME_INTERVAL_ENTIRE_RANGE:
    default:
      timePeriodOptions = [
        ...todayYesterdayTimePeriodOptions,
        ...(includeMinutely ? [...minutelyTimePeriodOptions, ...smallHourlyTimePeriodOptions] : []),
        ...largeHourlyTimePeriodOptions,
        ...dailyTimePeriodOptions,
        ...weeklyTimePeriodOptions,
        ...monthlyTimePeriodOptions,
        ...customTimePeriodOptions,
      ];
      break;
  }

  if (restrictLargeOrgs && showAlternateOptions) {
    timePeriodOptions = timePeriodOptions.filter(
      range => range.id != DateRangeID.Past90Days && range.id != DateRangeID.PastYear,
    );
  }

  if (includeAllTime) {
    timePeriodOptions.unshift(allTimeOption);
  }

  return maxDays > 0 ? timePeriodOptions.filter(range => range.days <= maxDays) : timePeriodOptions;
}

/**
 * Returns the previous/past TimeRange for the given TimeRange, if it exists.
 * @param timeRange current time range
 */
export function getPastTimeRange(timeRange?: SegmentTimeRange): SegmentTimeRange | null {
  if (timeRange) {
    const dateRange = getDateRangeIdFromTimeRange({
      Start: timeRange?.startTime,
      End: timeRange?.endTime,
    });
    switch (dateRange) {
      case DateRangeID.Past90Days:
        return { startTime: 'NOW/DAY-179DAY', endTime: 'NOW/DAY-89DAY' };
      case DateRangeID.PastMonth:
        return { startTime: 'NOW/DAY-59DAY', endTime: 'NOW/DAY-29DAY' };
      case DateRangeID.PastWeek:
        return { startTime: 'NOW/DAY-13DAY', endTime: 'NOW/DAY-6DAY' };
      case DateRangeID.Past24Hours:
        return { startTime: 'NOW/HOUR-47HOUR', endTime: 'NOW/HOUR-23HOUR' };
      case DateRangeID.Past60Minutes:
        return { startTime: 'NOW/MINUTE-119MINUTE', endTime: 'NOW/MINUTE-59MINUTE' };
      case DateRangeID.Past3Hours:
        return { startTime: 'NOW/HOUR-5HOUR', endTime: 'NOW/HOUR-2HOUR' };
      case DateRangeID.Past6Hours:
        return { startTime: 'NOW/HOUR-11HOUR', endTime: 'NOW/HOUR-5HOUR' };
      case DateRangeID.Yesterday:
        return { startTime: 'NOW/DAY-2DAY', endTime: 'NOW/DAY-1DAY' };
      case DateRangeID.Today:
        return { startTime: 'NOW/DAY-1DAY', endTime: 'NOW/DAY' };
      case DateRangeID.AllTime:
      case DateRangeID.Custom:
      case DateRangeID.Since:
      case DateRangeID.PastYear:
        return null;
      default:
        assertExhaustive(dateRange);
    }
  }
  return null;
}
